import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPinned, PackageCheck, Warehouse, Truck, Home, Activity } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { DASHBOARD_SIGNIN_URL, DASHBOARD_SIGNUP_URL } from '@/config/links';
import ProductFAQAccordion from '@/components/products/ProductFAQAccordion';
import WorkflowAnimations from '@/components/products/WorkflowAnimations';
import './DomesticLandingHero.css';

const domesticFaqs = [
  { question: 'How does domestic courier allocation work?', answer: 'Boxd auto-selects courier partners using pincode coverage, SLA performance, and shipping cost logic.' },
  { question: 'Can I ship both prepaid and COD orders?', answer: 'Yes, domestic workflows support COD and prepaid dispatch with full payment visibility.' },
  { question: 'Do you provide real-time shipment updates?', answer: 'Yes, every shipment milestone is trackable across pickup, hub movement, and final delivery.' },
  { question: 'Can I reduce failed deliveries?', answer: 'Yes, proactive updates and better courier selection help improve first-attempt delivery rates.' },
  { question: 'Is this suitable for high daily order volume?', answer: 'Yes, the platform is optimized for high-throughput eCommerce operations across India.' }
];

const ProductDomesticShipping = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Domestic Shipping Platform Across 25,000+ Pincodes"
        description="Automate domestic shipping in India with fast courier allocation, real-time tracking, and superior delivery performance."
        keywords="domestic shipping india, courier aggregator, ecommerce logistics platform, pincode delivery coverage"
        canonical="https://boxdlogistics.in/products/domestic-shipping"
      />
      <Navbar />

      <section className="domestic-hero relative overflow-hidden bg-gradient-to-b from-blue-100/60 via-blue-50 to-white px-4 pb-16 pt-28 md:pt-32">
        <div className="domestic-hero-backdrop pointer-events-none absolute inset-0">
          <div className="domestic-hero-line-top" />
          <div className="domestic-hero-grid-sm" />
          <div className="domestic-hero-grid-lg" />
          <div className="domestic-hero-orb-left" />
          <div className="domestic-hero-orb-right" />
          <div className="domestic-hero-radial" />
          <div className="domestic-geo-layer" aria-hidden>
            <div className="domestic-geo-square domestic-geo-bob domestic-geo-bob-a" />
            <div className="domestic-geo-circle domestic-geo-bob domestic-geo-bob-b" />
          </div>
        </div>

        <div className="relative z-10 mx-auto grid max-w-7xl gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="inline-flex rounded-full bg-white px-4 py-1 text-sm font-semibold text-blue-900 shadow-sm">Domestic Shipping</span>
            <h1 className="mt-4 text-4xl font-bold leading-tight text-gray-900 md:text-6xl">
              Deliver Faster Across India with Smarter Shipping Flows
            </h1>
            <p className="mt-5 max-w-xl text-lg text-gray-600">
              From seller pickup to final doorstep delivery, automate every shipment step with intelligent routing, tracking visibility, and COD-ready operations.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button className="bg-blue-800 text-white hover:bg-blue-900" onClick={() => { window.location.href = DASHBOARD_SIGNIN_URL; }}>
                Start Shipping <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" className="border-blue-300 text-blue-900 hover:bg-blue-50" asChild>
                <Link to="/contact">Book Demo</Link>
              </Button>
            </div>
          </div>

          <div className="relative z-10 rounded-3xl border border-blue-100 bg-white/90 p-6 shadow-xl backdrop-blur-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Live Shipment Journey</h2>
            <div className="space-y-3">
              {[
                { label: 'Order confirmed', icon: PackageCheck },
                { label: 'Order shipped', icon: Truck },
                { label: 'Order OFD', icon: MapPinned },
                { label: 'Delivered', icon: Home }
              ].map((event) => {
                const Icon = event.icon;
                return (
                  <div key={event.label} className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3">
                    <span className="rounded-lg bg-blue-100 p-2 text-blue-800"><Icon className="h-4 w-4" /></span>
                    <p className="text-sm font-medium text-gray-700">{event.label}</p>
                    <Activity className="ml-auto h-4 w-4 text-green-500" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-3xl font-bold text-gray-900 md:text-4xl">Shipment Workflow That Moves with Precision</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-gray-600 md:text-base">From order capture to doorstep delivery, every handoff is tracked, optimized, and built for scale.</p>
          <div className="mt-12 grid gap-5 md:grid-cols-4">
            {[
              { name: 'Seller', detail: 'Order synced instantly from storefront', icon: PackageCheck },
              { name: 'Shipped', detail: 'Pickup partner assigned by SLA and pincode', icon: Truck },
              { name: 'OFD', detail: 'Out for delivery with live tracking visibility', icon: MapPinned },
              { name: 'Delivered', detail: 'Doorstep delivery confirmed with proactive updates', icon: Home }
            ].map((step, index) => {
              const Icon = step.icon;
              const Animation = WorkflowAnimations[step.name];
              return (
                <div key={step.name} className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
                  <div className="relative h-40 w-full overflow-hidden bg-blue-50/40">
                    {Animation && <Animation />}
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-blue-800 shadow-sm">
                      Step {index + 1}
                    </span>
                    <span className="absolute bottom-3 left-3 inline-flex items-center justify-center rounded-lg bg-blue-800 p-2 text-white shadow-sm">
                      <Icon className="h-4 w-4" />
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl font-semibold text-gray-900">{step.name}</h3>
                    <p className="mt-2 text-sm text-gray-600">{step.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-b from-blue-50/70 to-white px-4 py-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-3xl font-bold text-gray-900 md:text-4xl">Domestic Shipping Metrics That Matter</h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { value: '25,000+', label: 'Pincodes serviceable' },
              { value: '98.4%', label: 'On-time dispatch rate' },
              { value: '24h', label: 'Fastest metro deliveries' },
              { value: '30%', label: 'Average shipping cost optimization' }
            ].map((metric) => (
              <div key={metric.label} className="rounded-2xl border border-blue-100 bg-white p-6 text-center shadow-sm">
                <p className="text-4xl font-bold text-blue-900">{metric.value}</p>
                <p className="mt-2 text-sm font-medium text-gray-600">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 pb-16">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-r from-blue-800 to-blue-900 px-8 py-12 text-center text-white">
          <h2 className="text-3xl font-bold md:text-4xl">Start Shipping Smarter Today</h2>
          <p className="mx-auto mt-3 max-w-2xl text-blue-100">Upgrade domestic fulfillment with a flow-first shipping system built for scale and speed.</p>
          <Button className="mt-7 bg-white text-blue-900 hover:bg-blue-50" onClick={() => { window.location.href = DASHBOARD_SIGNUP_URL; }}>
            Get Started <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <ProductFAQAccordion title="Domestic Shipping FAQs" items={domesticFaqs} />

      <Footer />
    </div>
  );
};

export default ProductDomesticShipping;
